/**
 * Workflow Serializer
 * Handles serialization and deserialization of workflow definitions
 */

import {
  WorkflowDefinition,
  SerializedWorkflow,
  SerializationOptions,
  DeserializationOptions,
  DeserializationResult,
  WorkflowNode,
  WorkflowEdge
} from './types';
import { WorkflowValidator } from './validator';

const SERIALIZATION_VERSION = '1.0.0';

export class WorkflowSerializer {
  /**
   * Serialize workflow definition to JSON string
   */
  static toJSON(
    workflow: WorkflowDefinition,
    options: SerializationOptions = {}
  ): string {
    const {
      pretty = false,
      includeMetadata = true,
      validate = true
    } = options;

    // Validate before serialization if requested
    if (validate) {
      const validationResult = WorkflowValidator.validate(workflow);
      if (!validationResult.valid) {
        throw new Error(
          `Workflow validation failed: ${validationResult.errors.map(e => e.message).join(', ')}`
        );
      }
    }

    // Create serialized structure
    const serialized: SerializedWorkflow = {
      version: SERIALIZATION_VERSION,
      workflow: includeMetadata ? workflow : this.stripMetadata(workflow)
    };

    // Convert to JSON string
    return pretty 
      ? JSON.stringify(serialized, null, 2)
      : JSON.stringify(serialized);
  }

  /**
   * Deserialize JSON string to workflow definition
   */
  static fromJSON(
    json: string,
    options: DeserializationOptions = {}
  ): DeserializationResult {
    const { validate = true, strict = false } = options;

    try {
      // Parse JSON
      const parsed = JSON.parse(json);

      // Check if it's a serialized workflow with version
      let workflow: WorkflowDefinition;
      if (parsed.version && parsed.workflow) {
        // Versioned format
        workflow = parsed.workflow;
      } else if (parsed.name && parsed.nodes && parsed.edges) {
        // Direct workflow format (backward compatibility)
        workflow = parsed;
      } else {
        return {
          success: false,
          error: 'Invalid workflow format: missing required fields'
        };
      }

      // Validate if requested
      if (validate) {
        const validationResult = WorkflowValidator.validate(workflow);
        
        if (!validationResult.valid) {
          return {
            success: false,
            error: `Validation failed: ${validationResult.errors.map(e => e.message).join(', ')}`,
            validationResult
          };
        }

        if (strict && validationResult.warnings.length > 0) {
          return {
            success: false,
            error: `Validation warnings in strict mode: ${validationResult.warnings.map(w => w.message).join(', ')}`,
            validationResult
          };
        }

        return {
          success: true,
          workflow,
          validationResult
        };
      }

      return {
        success: true,
        workflow
      };
    } catch (error: any) {
      return {
        success: false,
        error: `JSON parsing failed: ${error.message}`
      };
    }
  }

  /**
   * Convert ReactFlow nodes and edges to workflow definition
   */
  static fromReactFlow(
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    metadata?: Partial<WorkflowDefinition>
  ): WorkflowDefinition {
    return {
      name: metadata?.name || 'Untitled Workflow',
      description: metadata?.description,
      version: metadata?.version || 1,
      nodes,
      edges,
      configuration: metadata?.configuration,
      metadata: metadata?.metadata
    };
  }

  /**
   * Convert workflow definition to ReactFlow format
   */
  static toReactFlow(workflow: WorkflowDefinition): {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  } {
    return {
      nodes: workflow.nodes,
      edges: workflow.edges
    };
  }

  /**
   * Strip metadata from workflow for cleaner serialization
   */
  private static stripMetadata(workflow: WorkflowDefinition): WorkflowDefinition {
    const { metadata, ...workflowWithoutMetadata } = workflow;
    return workflowWithoutMetadata;
  }
}

